import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useWindows } from "@/context/WindowContext";
import { clamp } from "@/hooks/useIsMobile";
import { WindowSkeleton } from "@/components/ui/States";
import { EASE } from "@/components/ui/Reveal";

const TOP_OFFSET = 44;
const DOCK_OFFSET = 86;

function TrafficLight({ color, glyph, onClick, testid, label }) {
  return (
    <button
      data-testid={testid}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      className="group flex h-3.5 w-3.5 items-center justify-center rounded-full transition-transform duration-150 hover:scale-110"
      style={{ backgroundColor: color }}
    >
      <span className="font-mono text-[8px] font-bold leading-none text-black/60 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        {glyph}
      </span>
    </button>
  );
}

export default function Window({ app, win, children }) {
  const { focus, close, minimize, toggleMax, move, resize, topZ } = useWindows();
  const [mounting, setMounting] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setMounting(false), 420);
    return () => clearTimeout(t);
  }, []);

  const max = win.maximized;
  const active = win.z === topZ;

  const startDrag = (e) => {
    if (max || e.button !== 0) return;
    e.preventDefault();
    focus(app.id);
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = win.pos.x;
    const oy = win.pos.y;
    const onMove = (ev) => {
      move(app.id, {
        x: clamp(ox + ev.clientX - sx, -win.size.w + 150, window.innerWidth - 150),
        y: clamp(oy + ev.clientY - sy, 0, window.innerHeight - 70),
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    focus(app.id);
    const sx = e.clientX;
    const sy = e.clientY;
    const ow = win.size.w;
    const oh = win.size.h;
    const onMove = (ev) => {
      resize(app.id, {
        w: clamp(ow + ev.clientX - sx, 360, window.innerWidth - 24),
        h: clamp(oh + ev.clientY - sy, 300, window.innerHeight - TOP_OFFSET - 40),
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const style = max
    ? { left: 12, right: 12, top: 0, bottom: DOCK_OFFSET, width: "auto", height: "auto", zIndex: 100 + win.z }
    : { left: win.pos.x, top: win.pos.y, width: win.size.w, height: win.size.h, zIndex: 100 + win.z };

  return (
    <motion.div
      data-testid={`window-${app.id}`}
      className={`absolute flex flex-col overflow-hidden rounded-xl ${active ? "glass ring-1 ring-white/15" : "glass"}`}
      style={style}
      initial={{ opacity: 0, scale: 0.92, y: 28 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.94, y: 20, transition: { duration: 0.16, ease: "easeIn" } }}
      transition={{ duration: 0.3, ease: EASE }}
      onPointerDown={() => focus(app.id)}
    >
      <header
        data-testid={`window-header-${app.id}`}
        onPointerDown={startDrag}
        onDoubleClick={() => toggleMax(app.id)}
        className={`relative flex h-10 shrink-0 items-center border-b border-white/10 bg-white/[.05] px-3.5 ${max ? "" : "cursor-grab active:cursor-grabbing"} select-none`}
      >
        <div className="flex items-center gap-2">
          <TrafficLight color="#FF5F56" glyph="✕" label={`Close ${app.dockLabel}`} onClick={() => close(app.id)} testid={`window-close-${app.id}`} />
          <TrafficLight color="#FFBD2E" glyph="–" label={`Minimize ${app.dockLabel}`} onClick={() => minimize(app.id)} testid={`window-minimize-${app.id}`} />
          <TrafficLight color="#27C93F" glyph="+" label={`Maximize ${app.dockLabel}`} onClick={() => toggleMax(app.id)} testid={`window-maximize-${app.id}`} />
        </div>
        <div className="pointer-events-none absolute inset-x-0 flex items-center justify-center gap-2">
          <app.icon size={12} className="text-slate-400" />
          <p className="font-mono text-[11px] text-slate-400">{app.title}</p>
        </div>
        <div className="w-14" />
      </header>
      <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        {mounting ? <WindowSkeleton /> : children}
      </div>
      {!max && (
        <div
          onPointerDown={startResize}
          data-testid={`window-resize-${app.id}`}
          aria-label={`Resize ${app.dockLabel}`}
          className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
        />
      )}
    </motion.div>
  );
}
